"use client"

import { useEffect, useState, useMemo } from "react"
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Card,
  CardContent,
  Avatar,
  DialogActions,
  Button,
  Skeleton,
} from "@mui/material"
import {
  CheckCircle,
  History as HistoryIcon,
  Print,
  Visibility as VisibilityIcon,
  Image as ImageIcon,
  Delete as DeleteIcon,
  ChevronLeft,
  ChevronRight,
  RestartAlt as ResetIcon,
} from "@mui/icons-material"
import { useDispatch, useSelector } from "react-redux"
import { toast } from "react-toastify"
import moment from "moment"
import { deleteSale } from "../../redux/actions/sales"
import { fetchAllShops, selectShop } from "../../redux/actions/shop"
import ProductDetailsDialog from "../../components/ProductDetailsDialog"
import { generateReceipt } from "../../utils/receiptGenerator"
import { useInvoiceNumber } from "../../hooks/useInvoiceNumber"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment"
import { useTheme } from "@mui/material/styles"
import { tokens } from "../../theme"
import useSalesBatch from "../../hooks/useSalesBatch"

const ROWS_PER_PAGE = 50

const SkeletonRows = ({ count = 5, columns = 8 }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <TableRow key={i}>
        {Array.from({ length: columns }).map((_, j) => (
          <TableCell key={j}><Skeleton variant="text" /></TableCell>
        ))}
      </TableRow>
    ))}
  </>
)

const VenteParBank = () => {
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.user)
  const { shops, selectedShop } = useSelector((state) => state.shop)
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)

  const [openHistory, setOpenHistory] = useState(false)
  const [selectedSale, setSelectedSale] = useState(null)
  const [openProductDetails, setOpenProductDetails] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [searchInput, setSearchInput] = useState("")
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false)
  const [selectedImage, setSelectedImage] = useState("")

  const { getInvoiceNumber } = useInvoiceNumber()

  const electronicShops = shops.filter((shop) => shop.enum === "electronics" || shop.type === "electronics")

  const currentShopId = useMemo(() => {
    if (user?.role === "superadmin" || user?.role === "supervisor") {
      return selectedShop?._id || "all"
    }
    return typeof user?.shop === "object" ? user.shop._id : user?.shop
  }, [user, selectedShop])

  const filters = useMemo(() => ({
    paymentMethod: "bank",
    paymentStatus: "paid",
    startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : undefined,
    endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : undefined,
    search: searchQuery || undefined,
  }), [startDate, endDate, searchQuery])

  const { data: bankSales, isLoading, page, setPage, total, totalPages, invalidateCache } = useSalesBatch({
    shopId: currentShopId,
    filters,
    limit: ROWS_PER_PAGE,
  })

  useEffect(() => {
    if (user?.role === "superadmin" || user?.role === "supervisor") {
      dispatch(fetchAllShops())
    }
  }, [dispatch, user])

  const handleShopChange = async (event) => {
    const shopId = event.target.value
    if (shopId === "all") {
      dispatch(selectShop(null))
    } else {
      const shop = shops.find((s) => s._id === shopId)
      dispatch(selectShop(shop))
    }
  }

  const handleSearchSubmit = () => setSearchQuery(searchInput)
  const handleSearchKeyDown = (e) => { if (e.key === "Enter") handleSearchSubmit() }

  const handleResetFilters = () => {
    setSearchQuery("")
    setSearchInput("")
    setStartDate(null)
    setEndDate(null)
  }

  const handleDelete = async (saleId) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette vente ?")) {
      try {
        await dispatch(deleteSale(saleId))
        toast.success("Vente supprimée avec succès")
        invalidateCache()
        setPage(1)
      } catch (error) {
        toast.error(error.message || "Erreur lors de la suppression")
      }
    }
  }

  const handlePrint = async (sale) => {
    try {
      const shopId = sale.shop?._id || sale.shop
      const invoiceNumber = await getInvoiceNumber(shopId)
      const doc = await generateReceipt(sale, invoiceNumber)
      doc.save(`facture-${invoiceNumber}.pdf`)
    } catch (error) {
      toast.error("Erreur lors de l'impression de la facture")
      console.error("Print error:", error)
    }
  }

  const startRecord = (page - 1) * ROWS_PER_PAGE + 1
  const endRecord = Math.min(page * ROWS_PER_PAGE, total)

  return (
    <Box m="20px">
      <Card
        sx={{
          mb: 4, mt: 2,
          backgroundColor: theme.palette.mode === "dark" ? colors.primary[900] : colors.primary[100],
          color: theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[900],
        }}
      >
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ opacity: 0.9 }}>Total des ventes par banque</Typography>
          <Typography variant="h3" sx={{ fontWeight: "bold" }}>
            {total.toLocaleString()} <span style={{ fontSize: "1rem" }}>ventes</span>
          </Typography>
        </CardContent>
      </Card>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.mode === "dark" ? "text.primary" : "white" }}>
          Ventes bancaires payantes ({total || 0})
        </Typography>

        {(user.role === "superadmin" || user.role === "supervisor") && (
          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel>Boutique</InputLabel>
            <Select value={selectedShop ? selectedShop._id : "all"} onChange={handleShopChange} label="Boutique">
              <MenuItem value="all">Toutes les boutiques</MenuItem>
              {electronicShops.map((shop) => (
                <MenuItem key={shop._id} value={shop._id}>{shop.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)" }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            placeholder="Rechercher par client"
            variant="outlined"
            size="small"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            onBlur={handleSearchSubmit}
            sx={{
              flexGrow: 1, minWidth: 250,
              "& .MuiOutlinedInput-root": {
                borderRadius: 1.5,
                "& fieldset": { borderColor: "grey.300" },
                "&:hover fieldset": { borderColor: colors.primary[600] },
                "&.Mui-focused fieldset": { borderColor: colors.primary[600] },
              },
            }}
          />
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label="Date de début" value={startDate} onChange={(v) => setStartDate(v)}
              slotProps={{ textField: { size: "small", sx: { minWidth: 180 } } }}
            />
            <DatePicker
              label="Date de fin" value={endDate} onChange={(v) => setEndDate(v)}
              slotProps={{ textField: { size: "small", sx: { minWidth: 180 } } }}
            />
          </LocalizationProvider>
          <Tooltip title="Réinitialiser les filtres">
            <IconButton onClick={handleResetFilters} sx={{ color: colors.primary[500] }}><ResetIcon /></IconButton>
          </Tooltip>
        </Box>
      </Paper>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2, overflow: "auto", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
          backgroundColor: "background.paper", maxHeight: "calc(100vh - 420px)",
          "& .MuiTableRow-root": {
            "&:nth-of-type(odd)": { backgroundColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.02)" },
            "&:hover": { backgroundColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.04)" },
          },
          "& .MuiTableCell-root": {
            borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(81, 81, 81, 0.5)" : "rgba(224, 224, 224, 1)"}`,
            color: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.87)",
          },
          "& .MuiTableHead-root .MuiTableRow-root": {
            backgroundColor: theme.palette.mode === "dark" ? "rgb(30, 30, 30)" : "rgb(250, 250, 250)",
            "&:hover": { backgroundColor: theme.palette.mode === "dark" ? "rgb(30, 30, 30)" : "rgb(250, 250, 250)" },
            "& .MuiTableCell-root": { fontWeight: 600, backgroundColor: "transparent" },
          },
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Montant Total</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Montant payé</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Statut</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Preuve de paiement</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Effectué par</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <SkeletonRows count={10} columns={8} />
            ) : bankSales.length > 0 ? (
              bankSales.map((sale) => (
                <TableRow key={sale._id}>
                  <TableCell>{moment(sale.createdAt).format("DD/MM/YYYY HH:mm")}</TableCell>
                  <TableCell>{sale.client_name}</TableCell>
                  <TableCell sx={{ fontWeight: 500, color: colors.greenAccent[500] }}>
                    {sale.total_amount?.toLocaleString()} Fbu
                  </TableCell>
                  <TableCell>{sale.amount_paid?.toLocaleString()} Fbu</TableCell>
                  <TableCell>
                    {sale.payment_status === "paid" ? (
                      <Chip label="Payé" color="success" size="small" sx={{ fontWeight: 600 }} />
                    ) : (
                      <Chip label="Non payé" color="error" size="small" sx={{ fontWeight: 600 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    {sale.payment_proof?.url ? (
                      <Tooltip title="Voir la preuve de paiement">
                        <IconButton
                          onClick={() => { setSelectedImage(sale.payment_proof.url); setImagePreviewOpen(true) }}
                          size="small"
                        >
                          <Avatar src={sale.payment_proof.url} variant="rounded" sx={{ width: 40, height: 40, cursor: "pointer" }}>
                            <ImageIcon />
                          </Avatar>
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="textSecondary">Aucune preuve</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      <Tooltip title="Historique des paiements">
                        <IconButton onClick={() => { setSelectedSale(sale); setOpenHistory(true) }} size="small" sx={{ color: "inherit" }}>
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {user.role !== "supervisor" && (
                        <>
                          <Tooltip title="Voir les détails">
                            <IconButton onClick={() => { setSelectedSale(sale); setOpenProductDetails(true) }} size="small" sx={{ color: "inherit" }}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Imprimer la facture">
                            <IconButton onClick={() => handlePrint(sale)} size="small" sx={{ color: "inherit" }}>
                              <Print fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {user.role === "superadmin" && (
                            <Tooltip title="Supprimer">
                              <IconButton onClick={() => handleDelete(sale._id)} size="small" sx={{ color: "inherit", "&:hover": { backgroundColor: "rgba(255, 0, 0, 0.1)" } }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{sale.user?.name}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>Aucune vente bancaire trouvée</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} px={1}>
        <Typography variant="body2" color="text.secondary">
          {total > 0 ? `Affichage de ${startRecord}–${endRecord} sur ${total.toLocaleString()} ventes` : "Aucune vente"}
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Button size="small" startIcon={<ChevronLeft />} disabled={page <= 1 || isLoading} onClick={() => setPage(page - 1)} sx={{ textTransform: "none" }}>
            Précédent
          </Button>
          <Typography variant="body2" sx={{ mx: 1 }}>Page {page} sur {totalPages || 1}</Typography>
          <Button size="small" endIcon={<ChevronRight />} disabled={page >= totalPages || isLoading} onClick={() => setPage(page + 1)} sx={{ textTransform: "none" }}>
            Suivant
          </Button>
        </Box>
      </Box>

      {/* Payment History Dialog */}
      <Dialog open={openHistory} onClose={() => setOpenHistory(false)} maxWidth="md">
        <DialogTitle sx={{ backgroundColor: theme.palette.mode === "dark" ? colors.primary[900] : colors.primary[100], fontWeight: 600 }}>
          Historique des paiements
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: theme.palette.background.paper }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.mode === "dark" ? colors.primary[800] : colors.primary[200] }}>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Montant</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Méthode</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Effectué par</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedSale?.payment_history?.map((payment, index) => (
                  <TableRow key={index}>
                    <TableCell>{moment(payment.payment_date).format("DD/MM/YYYY HH:mm")}</TableCell>
                    <TableCell>{payment.amount?.toLocaleString()} Fbu</TableCell>
                    <TableCell>{payment.payment_method === "debt" ? "Avance avec dette" : payment.payment_method}</TableCell>
                    <TableCell>{payment.user?.name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: theme.palette.background.paper }}>
          <Button onClick={() => setOpenHistory(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      <ProductDetailsDialog
        open={openProductDetails}
        onClose={() => { setOpenProductDetails(false); setSelectedSale(null) }}
        products={selectedSale?.products || []}
      />

      {/* Image Preview Dialog */}
      <Dialog open={imagePreviewOpen} onClose={() => setImagePreviewOpen(false)} maxWidth="md">
        <DialogTitle sx={{ fontWeight: 600, backgroundColor: theme.palette.mode === "dark" ? colors.primary[900] : colors.primary[100] }}>
          Preuve de paiement
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: theme.palette.background.paper }}>
          <Box display="flex" justifyContent="center">
            <img src={selectedImage || "/placeholder.svg"} alt="Preuve de paiement" style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImagePreviewOpen(false)}>Fermer</Button>
          <Button component="a" href={selectedImage} target="_blank" rel="noopener noreferrer" color="primary">Ouvrir dans un nouvel onglet</Button>
          <Button
            onClick={() => {
              const link = document.createElement("a")
              link.href = selectedImage
              link.download = `preuve-paiement-${Date.now()}.jpg`
              document.body.appendChild(link)
              link.click()
              document.body.removeChild(link)
            }}
            color="primary" variant="contained"
          >
            Télécharger
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default VenteParBank
